import React, { useCallback, useEffect } from "react";
import { ILocation } from "../../models/master";
import { APP, DType, EAutoSearchFieldName, FORM_STYLES } from "../../constants";
import {
  Autocomplete,
  debounce,
  FormControl,
  Grid,
  InputLabel,
  TextField,
} from "@mui/material";
import { noOptionsText } from "../../helpers";
import { locationMasterApi } from "../../API/locationMaster";
import _ from "lodash";
import { IOvEntry } from "../../models/overage";
import { FormField } from "../../components/shared/form";

type Props = {
  osdDetails: any;
  formFieldsDisabled: boolean;
  formik: any;
  required?: boolean;
  formObject?: {
    cityField: string;
    zipField: string;
    cityLabel: string;
    zipLabel: string;
    stateField: string;
    stateLabel: string;
    destinationField?: string;
    destinationLabel?: string;
  };
};

const Location: React.FC<Props> = ({
  osdDetails,
  formFieldsDisabled,
  formObject,
  formik,
  required = false,
}: Props): JSX.Element => {
  const MAX_CITY_LENGTH = 20;
  const MAX_ZIP_LENGTH = 7;

  const [loading, setLoading] = React.useState<boolean>(true);
  const [totalPages, setTotalPages] = React.useState<number>(0);
  // Declare a new state variable for tooltips with a default empty object
  const [zipLocations, setZipLocations] = React.useState<ILocation[]>([]);
  const [cityLocations, setCityLocations] = React.useState<ILocation[]>([]);
  const [inputValues, setInputValues] = React.useState({
    zipInputValue: undefined,
    cityInputValue: undefined,
  });
  const [autocompleteValues, setAutocompleteValues] = React.useState<any>({
    zip: undefined,
    city: undefined,
  });

  const {
    zipField,
    cityField,
    stateField,
  }: {
    zipField?: keyof IOvEntry;
    cityField?: keyof IOvEntry;
    stateField?: keyof IOvEntry;
  } = formObject
    ? {
        zipField: formObject.zipField as keyof IOvEntry,
        cityField: formObject.cityField as keyof IOvEntry,
        stateField: formObject.stateField as keyof IOvEntry,
      }
    : {};

  const { handleBlur, setFieldValue, errors, touched } = formik;

  const getFieldValidationProps = (fieldName?: string) => {
    if (!fieldName) return { error: false, helperText: undefined as string | undefined };

    const isTouched = !!touched?.[fieldName];
    const hasValue = !!formik.values?.[fieldName];
    const message = errors?.[fieldName] as string | undefined;

    const showError = isTouched && !hasValue && !!message;

    return {
      error: showError,
      helperText: showError ? message : undefined,
    };
  };

  const updateInputValue = (key: string, value: string | undefined) => {
    setInputValues((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const updateAutocompleteValue = (key: string, value: any) => {
    setAutocompleteValues((prevState: any) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const handleLocationUpdate = (
    type: DType,
    locations: ILocation[],
    setFlag: string,
  ) => {
    const configMap: {
      [key: string]: {
        currentLocations: ILocation[];
        setLocationState: React.Dispatch<React.SetStateAction<ILocation[]>>;
        autocompleteKey: string;
      };
    } = {
      [DType.zip]: {
        currentLocations: zipLocations,
        setLocationState: setZipLocations,
        autocompleteKey: "zip",
      },
      [DType.city]: {
        currentLocations: cityLocations,
        setLocationState: setCityLocations,
        autocompleteKey: "city",
      },
    };

    const config = configMap[type];
    if (config) {
      updateLocationState(
        locations,
        setFlag,
        config.currentLocations,
        config.setLocationState,
        config.autocompleteKey,
        osdDetails?.[cityField as string],
        osdDetails?.[zipField as string],
      );
    }
  };

  const updateLocationState = (
    locations: ILocation[],
    setFlag: string,
    currentLocations: ILocation[],
    setLocationState: React.Dispatch<React.SetStateAction<ILocation[]>>,
    autocompleteKey: string,
    city: string | undefined,
    zip: string | undefined,
  ) => {
    setLocationState(
      setFlag === "pagination"
        ? [...currentLocations, ...locations]
        : locations || [],
    );
    if (setFlag === "onPageLoad") {
      updateAutocompleteValue(
        autocompleteKey,
        locations?.find((option) => option.CITY === city && option.ZIP === zip),
      );
    }
  };

  const handleScroll = (event: any, type: DType) => {
    const listboxNode = event.currentTarget;
    const position = listboxNode.scrollTop + listboxNode.clientHeight;
    let page = 0;
    let inputValue;
    if (listboxNode.scrollHeight - position <= 1) {
      if (type === DType.zip) {
        page = Math.floor(zipLocations.length / APP.overage.userAPILimit);
        inputValue = inputValues?.zipInputValue ?? "";
      } else if (type === DType.city) {
        page = Math.floor(cityLocations.length / APP.overage.userAPILimit);
        inputValue = inputValues?.cityInputValue ?? "";
      }
      if (page < totalPages) {
        getLocations(inputValue ?? "", type, "pagination", page + 1);
      }
    }
  };

  const getLocations = async (
    value: string,
    type: DType,
    setFlag: string = "onPageLoad",
    page?: number,
  ) => {
    if (!value || value.length < 3) {
      return;
    }

    const params = {
      search: value?.toString(),
      page: page ?? APP.overage.userAPIpageLimit,
      limit: APP.overage.userAPILimit,
    };

    try {
      const results = await locationMasterApi(params);
      setLoading(false);
      const locations: ILocation[] = results.data;
      setTotalPages(results.totalPages);

      handleLocationUpdate(type, locations, setFlag);
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  };

  const handleAutoCompleteOnChange = (value: any, type: DType) => {
    if (value) {
      const { ZIP, CITY, STATE } = value;
      updateAutocompleteValue(type === DType.zip ? "zip" : "city", value);
      setFieldValue(zipField, ZIP, true);
      setFieldValue(cityField, CITY, true);
      setFieldValue(stateField, STATE);
      if (formObject?.destinationField)
        setFieldValue(formObject.destinationField, value.TERMINALID);

      if (type === DType.zip) {
        updateInputValue("cityInputValue", CITY);
        setCityLocations([]);
        getLocations(CITY ?? "", DType.city);
      } else if (type === DType.city) {
        updateInputValue("zipInputValue", ZIP);
        setZipLocations([]);
        getLocations(ZIP ?? "", DType.zip);
      }
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((values, type) => getLocations(values, type), 1000),
    [],
  );

  const onAutoInputChange = (value: string, type?: DType) => {
    if (
      value === "undefined" ||
      (![cityField as string, zipField as string].includes(
        document?.activeElement?.id ?? "",
      ) &&
        value === undefined)
    )
      return;
    if (!type) return;

    const fieldMap = {
      [DType.city]: {
        setLocations: setCityLocations,
        inputKey: "cityInputValue",
      },
      [DType.zip]: {
        setLocations: setZipLocations,
        inputKey: "zipInputValue",
      },
    };

    const config = fieldMap[type as keyof typeof fieldMap];
    if (config) {
      config.setLocations([]);
      updateInputValue(config.inputKey, value);
      setLoading(true);
      debouncedSave(value, type);
    }
  };

  useEffect(() => {
    if (osdDetails && !_.isEmpty(osdDetails)) {
      setInputValues((prev) => ({
        ...prev,
        zipInputValue: osdDetails?.[zipField as string],
        cityInputValue: osdDetails?.[cityField as string],
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [osdDetails]);

  const ZipOption = (option: ILocation) => {
    return `${option.ZIP?.trim()} - ${option.CITY.trim()} - ${option.STATE.trim()} ${formObject?.destinationField ? " - " + option.TERMINALID : ""}`;
  };

  const CityOption = (option: ILocation) => {
    return `${option.CITY.trim()} -  ${option.ZIP?.trim()} - ${option.STATE.trim()} ${formObject?.destinationField ? " - " + option.TERMINALID : ""}`;
  };

  return (
    <>
      <Grid item md={3} xs={12}>
        <FormControl fullWidth size="small">
          <InputLabel style={FORM_STYLES.LABEL} shrink={true} id="shipperZip">
            {formObject?.zipLabel ?? "Zip"} <sup>{required ? "*" : ""}</sup>
          </InputLabel>
          <Autocomplete
            disablePortal
            disableClearable={true}
            size="small"
            id="zipcode"
            data-testid="zip-autocomplete"
            disabled={formFieldsDisabled}
            value={
              formik.values[zipField as string] ?? inputValues.zipInputValue
            }
            key={`${autocompleteValues.zip?.CITY}-${autocompleteValues.zip?.STATE}-`}
            inputValue={inputValues.zipInputValue ?? ""}
            onChange={(e, v) => handleAutoCompleteOnChange(v, DType.zip)}
            onBlur={() => {
              formik?.setFieldTouched(zipField as string, true, true);
            }}
            onInputChange={(e, v) => onAutoInputChange(v, DType.zip)}
            noOptionsText={noOptionsText(
              inputValues.zipInputValue,
              loading,
              EAutoSearchFieldName.zip,
              3,
            )}
            forcePopupIcon={false}
            className="autocomplete"
            options={zipLocations}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option?.ZIP || ""
            }
            isOptionEqualToValue={(option, value) => option?.ZIP === value?.ZIP}
            renderInput={(params) => (
              (() => {
                const validation = getFieldValidationProps(zipField as string);
                return (
              <TextField
                autoComplete="off"
                placeholder="Enter & Select option"
                onBlur={(e) => {
                  formik?.setFieldTouched(zipField as string, true, true);
                  handleBlur(e);
                }}
                {...params}
                variant="outlined"
                error={validation.error}
                helperText={validation.helperText}
                inputProps={{
                  ...params.inputProps,
                  "aria-label": "Shipper Zip",
                  "data-testid": zipField!,
                  maxLength: MAX_ZIP_LENGTH,
                }}
              />
                );
              })()
            )}
            renderOption={(props, option) => (
              <li {...props} key={ZipOption(option)} title={ZipOption(option)}>
                {ZipOption(option)}
              </li>
            )}
            ListboxProps={{
              onScroll: (e) => {
                handleScroll(e, DType.zip);
              },
            }}
          />
        </FormControl>
      </Grid>
      <Grid item md={3} xs={12}>
        <FormControl fullWidth size="small">
          <InputLabel style={FORM_STYLES.LABEL} shrink={true} id="shipperCity">
            {formObject?.cityLabel ?? "City"} <sup>{required ? "*" : ""}</sup>
          </InputLabel>
          <Autocomplete
            disableClearable={true}
            disablePortal
            size="small"
            id="city"
            data-testid="city-autocomplete"
            disabled={formFieldsDisabled}
            value={
              formik.values[cityField as string] ?? inputValues?.cityInputValue
            }
            key={`${autocompleteValues.city?.ZIP}-${autocompleteValues.city?.STATE}-`}
            inputValue={inputValues?.cityInputValue}
            onBlur={(e) => {
              formik?.setFieldTouched(cityField as string, true, true);
            }}
            onChange={(e, v) => handleAutoCompleteOnChange(v, DType.city)}
            onInputChange={(e, v) => onAutoInputChange(v, DType.city)}
            noOptionsText={noOptionsText(
              inputValues.cityInputValue,
              loading,
              EAutoSearchFieldName.city,
              3,
            )}
            forcePopupIcon={false}
            className="autocomplete"
            options={cityLocations}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option?.CITY || ""
            }
            isOptionEqualToValue={(option, value) =>
              option?.CITY === value?.CITY
            }
            renderInput={(params) => (
              (() => {
                const validation = getFieldValidationProps(cityField as string);
                return (
              <TextField
                autoComplete="off"
                placeholder="Enter & Select option"
                onBlur={(e) => {
                  formik?.setFieldTouched(cityField as string, true, true);
                  handleBlur(e);
                }}
                error={validation.error}
                helperText={validation.helperText}
                {...params}
                variant="outlined"
                inputProps={{
                  ...params.inputProps,
                  "aria-label": "Shipper City",
                  "data-testid": cityField,
                  maxLength: MAX_CITY_LENGTH,
                }}
              />
                );
              })()
            )}
            renderOption={(props, option) => (
              <li
                {...props}
                key={CityOption(option)}
                title={CityOption(option)}
              >
                {CityOption(option)}
              </li>
            )}
            ListboxProps={{
              onScroll: (e) => {
                handleScroll(e, DType.city);
              },
            }}
          />
        </FormControl>
      </Grid>

      <FormField
        id={formObject?.stateField ?? ""}
        label={`${formObject?.stateLabel}`}
        gridProps={{ md: formObject?.destinationField ? 1.5 : 3, xs: 12 }}
        disabled={true}
        error={getFieldValidationProps(formObject?.stateField).error}
        helperText={getFieldValidationProps(formObject?.stateField).helperText}
        value={
          autocompleteValues.zip?.STATE ??
          formik.values[formObject?.stateField as string] ??
          ""
        }
        required={required}
      />

      {formObject?.destinationField && (
        <FormField
          id={formObject.destinationField}
          gridProps={{ md: 1.5, xs: 12 }}
          disabled={true}
          label={formObject?.destinationLabel ?? "Destination"}
          error={getFieldValidationProps(formObject?.destinationField).error}
          required={required}
          helperText={getFieldValidationProps(formObject?.destinationField).helperText}
          value={
            autocompleteValues.zip?.TERMINALID ??
            (formObject?.destinationField
              ? formik.values[formObject.destinationField]
              : "") ??
            ""
          }
        />
      )}
    </>
  );
};

export default React.memo(Location);
